/**
 * OrbitPrint super admin — shared client, vocabulary and formatters.
 * Talks to /api/orbit/admin/* with the RETAIL super-admin token (agripos_token), like Sports.
 */
import axios from 'axios';
import { HOUSE } from '../PlatformAdminShell';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function orbitAdminApi() {
  const token = localStorage.getItem('agripos_token');
  return axios.create({ baseURL: `${BACKEND}/api/orbit/admin`, headers: { Authorization: `Bearer ${token}` } });
}

export const SHELVES = [
  { k: 'orgs', label: 'Organizations', to: '/superadmin/orbitprint/orgs' },
  { k: 'log', label: 'Log', to: '/superadmin/orbitprint/log' },
];

export const PLAN_CONFIG = {
  free:       { label: 'Free',       color: '#A3A3A3' },
  premium:    { label: 'Premium',    color: HOUSE.ok },
  enterprise: { label: 'Enterprise', color: '#B388FF' },
  founder:    { label: 'Founder',    color: HOUSE.gold },
};

export const STATUS_CONFIG = {
  active:       { label: 'Active',      color: HOUSE.ok },
  grace_period: { label: 'Grace',       color: HOUSE.warn },
  suspended:    { label: 'Suspended',   color: HOUSE.bad },
  deactivated:  { label: 'Deactivated', color: HOUSE.dim },
};

/** The attention vocabulary — the same words on the strip, the chips and the detail page. */
export const ATTENTION = {
  in_grace:      { label: 'In grace',        color: HOUSE.warn,  filter: 'grace' },
  expiring_soon: { label: 'Expiring soon',   color: HOUSE.amber, filter: 'expiring' },
  expired:       { label: 'Expired',         color: HOUSE.bad,   filter: 'expiring' },
  over_cap:      { label: 'Over plan cap',   color: HOUSE.info,  filter: 'over_cap' },
  suspended:     { label: 'Suspended',       color: HOUSE.bad,   filter: 'suspended' },
  deactivated:   { label: 'Deactivated',     color: HOUSE.dim,   filter: 'suspended' },
  never_expires: { label: 'No expiry date',  color: '#B388FF',   filter: 'all' },
  bad_expiry:    { label: 'Bad expiry date', color: HOUSE.bad,   filter: 'all' },
};

export const FILTERS = [
  { k: 'all', label: 'All' },
  { k: 'free', label: 'Free' },
  { k: 'premium', label: 'Premium' },
  { k: 'enterprise', label: 'Enterprise' },
  { k: 'founder', label: 'Founder' },
  { k: 'grace', label: 'Grace' },
  { k: 'expiring', label: 'Expiring' },
  { k: 'over_cap', label: 'Over cap' },
  { k: 'suspended', label: 'Suspended' },
];

export const ACTION_LABELS = {
  set_plan: 'Set plan', extend: 'Extended', suspended: 'Suspended', active: 'Activated', deactivated: 'Deactivated',
  notes: 'Notes edited', impersonate: 'Opened as owner',
};

export function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso.slice(0, 10);
  return d.toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString('en-PH', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function daysUntil(iso) {
  if (!iso) return null;
  const t = new Date(iso);
  if (isNaN(t)) return null;
  return Math.round((t - new Date()) / 86400000);
}

export function peso(n) {
  if (n === null || n === undefined || n === '') return '—';
  return `₱${Number(n).toLocaleString('en-PH')}`;
}

/** One honest line about the date that is IN EFFECT for this org (universal: show only what governs). */
export function dateInEffect(org) {
  if (org.plan === 'free') {
    if (org.downgraded_at) return { text: `On Free since ${fmtDate(org.downgraded_at)}`, color: HOUSE.mute };
    return { text: 'Free — no expiry', color: HOUSE.mute };
  }
  if (org.plan === 'founder') return { text: 'Founder — never expires', color: HOUSE.gold };
  if (org.account_status === 'grace_period') return { text: `Grace until ${fmtDate(org.grace_until)} · then Free`, color: HOUSE.warn };
  if (org.account_status === 'suspended') return { text: `Suspended ${fmtDate(org.suspended_at)}`, color: HOUSE.bad };
  if (!org.plan_expires_at) return { text: 'Negotiated — never expires', color: '#B388FF' };
  const d = daysUntil(org.plan_expires_at);
  const color = d < 0 ? HOUSE.bad : d <= 7 ? HOUSE.warn : HOUSE.ok;
  const tail = d < 0 ? `${-d} day${-d === 1 ? '' : 's'} overdue` : d === 0 ? 'expires today' : `${d} day${d === 1 ? '' : 's'} left`;
  return { text: `Expires ${fmtDate(org.plan_expires_at)} · ${tail}`, color };
}

/** Public plan table (no auth) — the reference card inside the set-plan modal. */
export function fetchPlans() {
  return axios.get(`${BACKEND}/api/orbit/plans`).then(r => r.data);
}

/** Where the OrbitPrint APP lives from here: its own host in production, /orbit on a dev box. */
export function orbitAppOrigin() {
  const { protocol, host, hostname } = window.location;
  if (hostname.startsWith('orbitprint.')) return `${protocol}//${host}`;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || /^dev/i.test(hostname)) return `${protocol}//${host}/orbit`;
  return 'https://orbitprint.agri-books.com';
}

/** Product-specific hash key: a bare `support_token` would be eaten by the Sports auth module. */
export function supportUrl(token) {
  return `${orbitAppOrigin()}/#orbit_support_token=${encodeURIComponent(token)}`;
}

export function errorKind(e) {
  const s = e?.response?.status;
  if (s === 401) return 'denied';
  if (s === 403) return 'denied';
  if (s === 404) return 'notfound';
  return 'error';
}
